// SelectionCategoryTree — read-only category tree for left-panel selection.
// Reused by ControlLayout, ActionLayout, EventLayout, and ObligationLayout.
// For the editable tree (framework config), see category-tree/CategoryTreeEditor.

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  categoriesApi,
  buildCategoryTree,
  type CategoryType,
  type CategoryTreeNode,
} from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SelectionCategoryTreeProps {
  categoryType: CategoryType
  selectedCategoryId: string | null
  onSelectCategory: (categoryId: string, categoryName: string) => void
}

export function SelectionCategoryTree({
  categoryType,
  selectedCategoryId,
  onSelectCategory,
}: SelectionCategoryTreeProps) {
  const api = useApi()
  const { slug } = useTenant()
  const catApi = categoriesApi(api, categoryType)

  const {
    data: categories,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.categories.tree(slug, categoryType),
    queryFn: () => catApi.tree(),
  })

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const hasAutoExpanded = useRef(false)

  const tree = categories ? buildCategoryTree(categories) : []

  // Auto-expand root and depth-1 nodes on first load
  useEffect(() => {
    if (tree.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true
      const initial = new Set<string>()
      function expand(nodes: CategoryTreeNode[]) {
        for (const n of nodes) {
          if (n.depth <= 1) {
            initial.add(n.id)
            expand(n.children)
          }
        }
      }
      expand(tree)
      setExpandedIds(initial)
    }
  }, [categories]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-7 animate-pulse rounded bg-muted/50" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3">
        <ErrorState
          message="Failed to load categories"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (tree.length === 0) {
    return (
      <div className="p-3 text-center text-sm text-muted-foreground">
        No categories found.
      </div>
    )
  }

  return (
    <div className="py-1">
      {tree.map((root) => (
        <CategoryNode
          key={root.id}
          node={root}
          selectedId={selectedCategoryId}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          onSelect={onSelectCategory}
        />
      ))}
    </div>
  )
}

// --- Recursive tree node ---

function CategoryNode({
  node,
  selectedId,
  expandedIds,
  onToggle,
  onSelect,
}: {
  node: CategoryTreeNode
  selectedId: string | null
  expandedIds: Set<string>
  onToggle: (id: string) => void
  onSelect: (id: string, name: string) => void
}) {
  const hasChildren = node.has_children || node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const indent = node.depth * 16

  return (
    <div>
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm transition-colors',
          'hover:bg-accent/50',
          isSelected && 'bg-primary/10 font-medium text-primary',
        )}
        style={{ paddingLeft: `${indent + 8}px` }}
        onClick={() => onSelect(node.id, node.name)}
      >
        {/* Expand/collapse */}
        {hasChildren ? (
          <button
            className="flex h-4 w-4 shrink-0 items-center justify-center"
            onClick={(e) => {
              e.stopPropagation()
              onToggle(node.id)
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Folder icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}

        {/* Name */}
        <span className="truncate">{node.name}</span>

        {/* Object count */}
        {node.object_count > 0 && (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            {node.object_count}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <CategoryNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}
